import React, { useState, useCallback, useRef, useEffect } from 'react';
import SearchBox from '../components/SearchBox';
import SearchResults from '../components/SearchResults';
import { searchApi } from '../api/searchApi';
import { LearningCenterQueryData } from '../types';

interface LearningCenterSearchProps {
  searchPlaceholder?: string;
}

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;
const MIN_EMPTY_DELAY = 300;
const ACTIVE_CLASS = 'is-active';

const LearningCenterSearch: React.FC<LearningCenterSearchProps> = ({
  searchPlaceholder = 'Search...',
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LearningCenterQueryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const pageId =
    document.getElementById('learning-center-search')?.getAttribute('data-page-id') || '';

  const wrapperRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);
  const reqStartTsRef = useRef(0);

  const items = results?.Content?.items ?? [];
  const clampIndex = (i: number) =>
    items.length === 0 ? -1 : ((i % items.length) + items.length) % items.length;

  const openItem = (idx: number) => {
    if (idx < 0 || idx >= items.length) return;
    const url = (items[idx] as any).Url || (items[idx] as any).url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const highlightDomRow = (idx: number) => {
    const list = document.querySelectorAll<HTMLLIElement>(
      '#learning-center-search-results .search-result-item'
    );
    list.forEach(li => li.classList.remove(ACTIVE_CLASS));
    if (idx >= 0 && list[idx]) list[idx].classList.add(ACTIVE_CLASS);
    // Scroll into view if needed
    if (idx >= 0 && list[idx]) {
      list[idx].scrollIntoView({ block: 'nearest' });
    }
  };

  const fetchSearchResults = useCallback(
    async (searchQuery: string) => {
      const trimmed = searchQuery.trim();

      if (trimmed.length < MIN_CHARS) {
        setResults(null);
        setDropdownOpen(false);
        setLoading(false);
        setError(null);
        setActiveIndex(-1);
        return;
      }

      const myReqId = ++requestIdRef.current;
      reqStartTsRef.current = performance.now();
      setLoading(true);
      setError(null);

      try {
        const data = await searchApi(trimmed, pageId);
        if (myReqId !== requestIdRef.current) return;

        const filteredItems = (data?.Content?.items || []).filter((item: any) => {
          if (
            item.__typename === 'RedesignArticlePage' ||
            item.__typename === 'RedesignPrimaryPage' ||
            item.__typename === 'RedesignTopicPage'
          ) {
            return item.ArticleTitle?.trim() !== '';
          }
          return true;
        });

        const filteredData: LearningCenterQueryData = {
          ...data,
          Content: { ...data.Content, items: filteredItems },
        };

        const elapsed = performance.now() - reqStartTsRef.current;
        if (elapsed < MIN_EMPTY_DELAY) {
          await new Promise(r => setTimeout(r, MIN_EMPTY_DELAY - elapsed));
        }

        setResults(filteredData);
        setDropdownOpen(true);
        setActiveIndex(filteredItems.length ? 0 : -1);
      } catch (e) {
        if (myReqId === requestIdRef.current) {
          setError('Failed to fetch results');
          setResults(null);
          setDropdownOpen(true);
          setActiveIndex(-1);
        }
      } finally {
        if (myReqId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [pageId]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSearchResults(query);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, fetchSearchResults]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
        setQuery('');
        setResults(null);
        setError(null);
        setLoading(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard nav
  useEffect(() => {
    if (!isDropdownOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(document.activeElement as Node)
      ) {
        return;
      }

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const next = clampIndex(activeIndex + 1);
          setActiveIndex(next);
          highlightDomRow(next);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prev = clampIndex(activeIndex - 1);
          setActiveIndex(prev);
          highlightDomRow(prev);
          break;
        }
        case 'Enter': {
          if (activeIndex >= 0) {
            e.preventDefault();
            setDropdownOpen(false);
            openItem(activeIndex);
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          setDropdownOpen(false);
          setActiveIndex(-1);
          break;
        }
        default:
          break;
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDropdownOpen, activeIndex, items.length]);

  useEffect(() => {
    highlightDomRow(activeIndex);
  }, [activeIndex, results]);

  const showDropdown = isDropdownOpen && query.trim().length >= MIN_CHARS;
  const hasItems = items.length > 0;

  return (
    <div id="learning-center-search" ref={wrapperRef}>
      <SearchBox
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onSearch={(q) => fetchSearchResults(q)}
        searchPlaceholder={searchPlaceholder}
      />

      {showDropdown && (
        <>
          {loading && (
            <div id="learning-center-search-results" role="status" aria-busy="true">
              <ul>
                <li className="search-result-item">
                  <p>Searching…</p>
                </li>
              </ul>
            </div>
          )}

          {!loading && error && (
            <div id="learning-center-search-results">
              <ul>
                <li className="search-result-item">
                  <p>{error}</p>
                </li>
              </ul>
            </div>
          )}

          {!loading && !error && results && (
            hasItems ? (
              <SearchResults results={results} />
            ) : (
              <div id="learning-center-search-results">
                <ul>
                  <li className="search-result-item">
                    <p>No results found.</p>
                  </li>
                </ul>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default LearningCenterSearch;
